<script setup>
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  assignee: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['click', 'delete']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const navigateToConversation = () => {
  const accountId = route.params.accountId;
  const conversationId = props.card.conversation_id;
  if (accountId && conversationId) {
    router.push({
      name: 'inbox_conversation',
      params: { accountId, conversation_id: conversationId },
    });
  }
};
</script>

<template>
  <article
    class="group relative flex flex-col gap-1 p-3 rounded-lg border border-n-strong bg-n-surface-1 dark:border-n-strong/50 cursor-pointer transition-colors hover:border-n-slate-8 dark:hover:border-n-slate-6"
    @click="emit('click')"
  >
    <p class="text-sm font-medium text-n-slate-12 break-words">
      {{ card.title }}
    </p>
    <p
      v-if="card.description"
      class="text-xs text-n-slate-10 line-clamp-2 whitespace-pre-line"
    >
      {{ card.description }}
    </p>
    <div
      v-if="assignee || card.conversation_id"
      class="flex items-center gap-1.5 mt-1"
    >
      <Avatar
        v-if="assignee"
        :name="assignee.name"
        :src="assignee.thumbnail"
        :size="16"
        rounded-full
        :title="t('KANBAN.CARDS.ASSIGNED_TO', { name: assignee.name })"
      />
      <span
        v-if="card.conversation_id"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-alpha-3/50 text-xs text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-brand cursor-pointer"
        :title="t('KANBAN.CARDS.LINKED_CONVERSATION')"
        @click.stop="navigateToConversation"
      >
        <Icon icon="i-lucide-message-square" class="size-3" />
        {{ `#${card.conversation_id}` }}
      </span>
    </div>
    <span
      v-if="card.manual_override"
      class="absolute top-1.5 right-8 flex items-center justify-center p-1 rounded-md text-n-slate-9"
      :title="t('KANBAN.CARDS.LOCK_BADGE')"
    >
      <Icon icon="i-lucide-lock" class="size-3.5" />
    </span>
    <span
      v-if="card.eligible === false"
      class="absolute top-1.5 right-14 flex items-center justify-center p-1 rounded-md text-n-ruby-9"
      :title="t('KANBAN.CARDS.INELIGIBLE_BADGE')"
    >
      <Icon icon="i-lucide-ban" class="size-3.5" />
    </span>
    <button
      type="button"
      class="absolute top-1.5 right-1.5 flex items-center justify-center p-1 rounded-md text-n-slate-9 opacity-0 transition-opacity hover:bg-n-alpha-2 hover:text-n-ruby-9 group-hover:opacity-100"
      :aria-label="t('KANBAN.CARDS.DELETE_TITLE')"
      :title="t('KANBAN.CARDS.DELETE_TITLE')"
      @click.stop="emit('delete')"
    >
      <Icon icon="i-lucide-trash" class="size-3.5" />
    </button>
  </article>
</template>
